
// src/components/InvoiceForm.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import "./invoice-form.css";

const API_BASE = process.env.REACT_APP_API_BASE || "https://dairy.server.statify.co.ke";

/* Enhanced accordion section with modern styling */
function Section({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  function toggle() { setOpen(v => !v); }
  function onKey(e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }
  return (
    <div className="section">
      <div
        role="button"
        tabIndex={0}
        className={`section-header ${open ? "open" : ""}`}
        onClick={toggle}
        onKeyDown={onKey}
        aria-expanded={open}
      >
        <div className="section-title">{title}</div>
        <div className={`section-chevron ${open ? "open" : ""}`} aria-hidden="true">▾</div>
      </div>

      <div className={`section-body ${open ? "open" : ""}`} aria-hidden={!open}>
        <div className="section-body-inner">{children}</div>
      </div>
    </div>
  );
}

function InvoiceFormContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const emptyLine = () => ({ product_id: null, product_name: "", qty: 1, unit_cost: 0, total_amount: 0 });

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [form, setForm] = useState({
    invoice_number: "", // existing invoice id if editing
    customer_id: null,
    buyer_name: "",
    // initialize to current datetime (ISO) — but user cannot edit this field in the form UI
    invoice_datetime: new Date().toISOString(),
    description: "",
    account_id: null,
    account_label: "",
    sales_person: "",
    lines: [emptyLine()],
    total_amount: 0,
    payment_status: "pending"
  });

  const [contrib, setContrib] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);

  // Invoice list (rendered below form)
  const [invoices, setInvoices] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    // load reference data
    fetch(`${API_BASE}/api/customers/`).then(r => r.ok ? r.json() : []).then(setCustomers).catch(()=>{});
    fetch(`${API_BASE}/api/products/`).then(r => r.ok ? r.json() : []).then(setProducts).catch(()=>{});
    fetch(`${API_BASE}/api/staff/`).then(r => r.ok ? r.json() : []).then(setStaff).catch(()=>{});
    fetch(`${API_BASE}/api/accounts/`)
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (list && list.length) setAccounts(list);
        else setAccounts([
          { id: 1310, code: "1310", label: "1310 - Debtors - SSL" },
          { id: 4000, code: "4000", label: "4000 - Sales - Local" }
        ]);
      })
      .catch(()=>{});

    loadInvoices();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    fetch(`${API_BASE}/api/sales/${id}`)
      .then(r => { if (!r.ok) throw new Error("failed to load sale"); return r.json(); })
      .then(data => {
        setForm(prev => ({
          ...prev,
          invoice_number: data.invoice_number ?? prev.invoice_number,
          customer_id: data.customer_id ?? null,
          buyer_name: data.buyer_name ?? "",
          // preserve whatever the server returned (if present) else keep current value
          invoice_datetime: data.date ?? prev.invoice_datetime,
          description: data.notes ?? prev.description,
          account_id: data.accounting_details?.account_id ?? prev.account_id,
          account_label: data.accounting_details?.account_label ?? prev.account_label,
          sales_person: data.sales_person ?? prev.sales_person,
          payment_status: data.payment_status ?? prev.payment_status,
          lines: Array.isArray(data.sale_items) && data.sale_items.length ? data.sale_items.map(li => ({
            product_id: li.product_id ?? null,
            product_name: li.product_name ?? (li.product?.name || ""),
            qty: Number(li.quantity ?? 1),
            unit_cost: Number(li.price_per_unit ?? 0),
            total_amount: Number(li.amount ?? (Number(li.quantity || 0) * Number(li.price_per_unit || 0)))
          })) : [emptyLine()],
        }));
        if (Array.isArray(data.contributions)) setContrib(data.contributions);
      })
      .catch(err => { console.error(err); alert("Failed to load sale: " + (err.message || err)); })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // compute totals
  const saleTotal = useMemo(() => {
    const t = (form.lines || []).reduce((s, l) => {
      const qty = Number(l.qty || 0);
      const uc = Number(l.unit_cost || 0);
      const amt = Number(l.total_amount != null ? l.total_amount : qty * uc) || 0;
      return s + amt;
    }, 0);
    return Number(t.toFixed(2));
  }, [form.lines]);

  useEffect(() => setForm(prev => ({ ...prev, total_amount: saleTotal })), [saleTotal]);

  function setField(key, value) { setForm(prev => ({ ...prev, [key]: value })); }

  function updateLine(idx, patch) {
    setForm(prev => {
      const lines = prev.lines.map((l,i) => i === idx ? { ...l, ...patch } : l);
      if (patch.qty !== undefined || patch.unit_cost !== undefined) {
        const q = Number(lines[idx].qty || 0), uc = Number(lines[idx].unit_cost || 0);
        lines[idx].total_amount = Number((q * uc).toFixed(2));
      }
      return { ...prev, lines };
    });
  }
  function addLine(prefill) { setForm(prev => ({ ...prev, lines: [...(prev.lines||[]), prefill || emptyLine()] })); }
  function removeLine(idx) { setForm(prev => ({ ...prev, lines: prev.lines.filter((_,i) => i !== idx) || [emptyLine()] })); }
  function pickProduct(idx, pid) {
    const p = products.find(x => String(x.id) === String(pid));
    if (!p) return updateLine(idx, { product_id: null, product_name: "", unit_cost: 0, total_amount: 0 });
    updateLine(idx, { product_id: p.id, product_name: p.name, unit_cost: Number(p.standard_rate || 0) });
  }

  function addContribRow() { setContrib(prev => [...prev, { id: Date.now(), sales_person: "", contribution_pct: 0, commission_rate: 0, incentives: "" }]); }
  function updateContribRow(i, patch) { setContrib(prev => prev.map((r,idx) => idx === i ? { ...r, ...patch } : r)); }
  function removeContribRow(i) { setContrib(prev => prev.filter((_,idx) => idx !== i)); }
  function contributionAmount(row) { return Number(((Number(row.contribution_pct||0)/100) * saleTotal).toFixed(2)); }

  function AccountSelector({ accounts, value, onSelect }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const filtered = accounts.filter(a => !query || (a.label || "").toLowerCase().includes(query.toLowerCase()));
    return (
      <div className="account-picker">
        <input
          className="input"
          value={query || (accounts.find(a=>a.id===value)?.label || "")}
          placeholder="Select account..."
          onFocus={() => setOpen(true)}
          onChange={e => setQuery(e.target.value)}
        />
        {open && (
          <div className="account-list" onMouseLeave={() => setOpen(false)}>
            {filtered.length ? filtered.map(a => (
              <div className="account-item" key={a.id} onMouseDown={(ev)=>{ ev.preventDefault(); onSelect(a); setOpen(false); setQuery(""); }}>
                <div className="account-item-title">{a.label}</div>
                <div className="account-item-sub">{a.code ?? a.id}</div>
              </div>
            )) : <div className="account-empty">No matches</div>}
            <div className="account-actions">
              <button type="button" className="btn-ghost small-btn" onMouseDown={(e)=>{ e.preventDefault(); const lbl = prompt("New account label:"); if(lbl){ const newA = { id: Date.now(), code: String(Date.now()).slice(-4), label: lbl }; setAccounts(prev => [newA, ...prev]); onSelect(newA); }}}>+ Create new</button>
              <button type="button" className="btn-ghost small-btn" onMouseDown={(e)=>{ e.preventDefault(); alert("Advanced search not implemented"); }}>Search</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Robust invoice list loader ---
  async function loadInvoices() {
    setLoadingInvoices(true);
    try {
      const res = await fetch(`${API_BASE}/api/sales/`);
      if (!res.ok) {
        const text = await res.text().catch(()=>"");
        console.error("Failed to fetch invoices:", res.status, text);
        setInvoices([]);
        return;
      }
      const list = await res.json();
      setInvoices(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Network error loading invoices:", err);
      setInvoices([]);
      alert("Unable to load invoices. Check API server and CORS settings.");
    } finally {
      setLoadingInvoices(false);
    }
  }

  // helper to try both trailing-slash and non-trailing forms for DELETE
  async function tryDeleteUrlVariants(baseUrl, options = {}) {
    const urlsToTry = [baseUrl, baseUrl.replace(/\/$/, "")];
    let lastErr = null;
    for (const url of urlsToTry) {
      try {
        const res = await fetch(url, options);
        // if we get an HTTP response, return it (even if 4xx/5xx)
        return res;
      } catch (err) {
        lastErr = err;
        console.warn("DELETE attempt failed for", url, err);
      }
    }
    throw lastErr || new Error("Unknown delete failure");
  }

  // --- Delete invoice with helpful errors ---
  async function handleDeleteInvoice(invId) {
    if (!invId) return alert("Invalid invoice id");
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;

    setDeletingId(invId);
    try {
      const baseUrl = `${API_BASE}/api/sales/${invId}/`;
      let res;
      try {
        res = await tryDeleteUrlVariants(baseUrl, { method: "DELETE", headers: { "Content-Type": "application/json" } });
      } catch (networkErr) {
        console.error("Network/CORS error while deleting invoice:", networkErr);
        alert("Delete failed: Network error. Check API server is running and CORS settings.");
        return;
      }

      if (!res.ok) {
        const text = await res.text().catch(()=>"");
        try { const j = JSON.parse(text || "{}"); throw new Error(j.error || j.message || text || res.statusText); } catch { throw new Error(text || res.statusText); }
      }

      alert("Invoice deleted");
      await loadInvoices();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed: " + (err.message || err));
    } finally {
      setDeletingId(null);
    }
  }

  // when user picks a customer, auto-populate buyer_name if empty
  function onCustomerChange(value) {
    const cust = customers.find(c => String(c.id) === String(value));
    const custId = value === "" ? null : Number(value);
    setForm(prev => {
      const buyer = prev.buyer_name && prev.buyer_name.trim() ? prev.buyer_name : (cust ? cust.name : "");
      return { ...prev, customer_id: custId, buyer_name: buyer };
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    // ensure buyer_name exists (backend expects it)
    let finalBuyer = form.buyer_name && form.buyer_name.trim() ? form.buyer_name.trim() : "";
    if (!finalBuyer && form.customer_id) {
      const c = customers.find(x => Number(x.id) === Number(form.customer_id));
      if (c) finalBuyer = c.name || "";
    }
    if (!finalBuyer) return alert("Please select a customer or enter buyer name");

    setSaving(true);
    try {
      // For create: set the current datetime automatically (ISO string).
      // For edit: preserve the existing form.invoice_datetime (loaded from server).
      const invoiceDate = isEdit ? form.invoice_datetime : new Date().toISOString();

      // NOTE: For create we DO NOT send invoice_number (ID) so server can autogenerate.
      const payload = {
        customer_id: form.customer_id,
        buyer_name: finalBuyer,
        payment_status: form.payment_status || "pending",
        date: invoiceDate,
        notes: form.description,
        accounting_details: { account_id: form.account_id, account_label: form.account_label },
        sales_person: form.sales_person,
        total_amount: saleTotal,
        sale_items: form.lines.map(l => ({ product_id: l.product_id, product_name: l.product_name, quantity: Number(l.qty), price_per_unit: Number(l.unit_cost), amount: Number(l.total_amount) })),
        contributions: contrib.map(r => ({ sales_person: r.sales_person, contribution_pct: Number(r.contribution_pct), contribution_amount: contributionAmount(r), commission_rate: Number(r.commission_rate), incentives: r.incentives }))
      };

      const url = isEdit ? `${API_BASE}/api/sales/${id}/` : `${API_BASE}/api/sales/`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });

      if (!res.ok) {
        const txt = await res.text().catch(()=>"");
        try { const j = JSON.parse(txt||"{}"); throw new Error(j.error || j.message || txt || res.statusText); } catch { throw new Error(txt || res.statusText); }
      }

      // For create, server may return the created invoice including the generated invoice_number.
      if (!isEdit) {
        try {
          const created = await res.json().catch(()=>null);
          if (created) {
            if (created.invoice_number) setForm(prev => ({ ...prev, invoice_number: created.invoice_number }));
            if (created.date) setForm(prev => ({ ...prev, invoice_datetime: created.date }));
            else setForm(prev => ({ ...prev, invoice_datetime: invoiceDate }));
          } else {
            // set our locally generated date for the UI
            setForm(prev => ({ ...prev, invoice_datetime: invoiceDate }));
          }
        } catch (err) {
          setForm(prev => ({ ...prev, invoice_datetime: invoiceDate }));
        }
      }

      setShowReceipt(true);
      await loadInvoices();
    } catch (err) {
      console.error(err);
      alert("Save failed: " + (err.message || err));
    } finally {
      setSaving(false);
    }
  }

  function printReceipt() { window.print(); }
  function viewInvoice(invId) { navigate(`/sales/${invId}`); }
  function editInvoice(invId) { navigate(`/sales/${invId}/edit`); }

  return (
    <div className={`form-card ${loading ? "loading" : ""}`}>
      <h2>{isEdit ? "Edit Invoice" : "New Invoice"}</h2>

      <form onSubmit={handleSave} className="form-grid">
        {/* === Header section === */}
        <Section title="Header" defaultOpen={true}>
          <div className="form-grid form-grid-3">
            {/* Invoice ID (hidden for create; readonly visible for edit) */}
            {isEdit && form.invoice_number && (
              <div className="field">
                <label>Invoice ID</label>
                <div className="muted">{form.invoice_number}</div>
              </div>
            )}

            <div className="field">
              <label>Invoice Date</label>
              {/* Date/time is automatic — display readonly value for information */}
              <div className="muted">{form.invoice_datetime ? new Date(form.invoice_datetime).toLocaleString() : "-"}</div>
            </div>

            <div className="field">
              <label>Customer</label>
              <select className="input" value={form.customer_id ?? ""} onChange={e => onCustomerChange(e.target.value)}>
                <option value="">-- Select customer --</option>
                {customers.map(c => <option key={c.id} value={c.id}>{`${c.id} — ${c.name}`}</option>)}
              </select>
            </div>

            <div className="field">
              <label>Buyer name (free text)</label>
              <input className="input" value={form.buyer_name} onChange={e => setField("buyer_name", e.target.value)} placeholder="If not linked to a customer" />
            </div>

            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Description</label>
              <textarea className="textarea" rows={3} value={form.description} onChange={e => setField("description", e.target.value)} placeholder="Invoice description, notes, tax details..." />
            </div>
          </div>
        </Section>

        {/* === Lines section === */}
        <Section title="Lines" defaultOpen={true}>
          <div style={{ marginTop: 8 }}>
            <table className="compact-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: 48 }}>Line No</th>
                  <th style={{ width: 120 }}>Invoice ID</th>
                  <th>Product (ID &amp; Name)</th>
                  <th style={{ width: 120 }}>Quantity</th>
                  <th style={{ width: 140 }}>Unit Cost</th>
                  <th style={{ width: 140 }}>Total Amount</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{form.invoice_number || "-"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexDirection: "column" }}>
                        <select className="input" value={line.product_id ?? ""} onChange={e => pickProduct(idx, e.target.value)}>
                          <option value="">-- select product --</option>
                          {products.map(p => <option key={p.id} value={p.id}>{`${p.id} — ${p.name}`}</option>)}
                        </select>

                        {/* {line.product_id ? (
                          <div className="muted" style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1px solid var(--border)", background: "#fff" }}>
                            {line.product_name || "-"}
                          </div>
                        ) : (
                          <input className="input" style={{ marginTop: 8 }} value={line.product_name} onChange={e => updateLine(idx, { product_name: e.target.value })} placeholder="Product name" />
                        )} */}
                      </div>
                    </td>
                    <td>
                      <input className="input" type="number" min="0" step="0.01" value={line.qty} onChange={e => updateLine(idx, { qty: Number(e.target.value) })} />
                    </td>
                    <td>
                      <input className="input" type="number" min="0" step="0.01" value={line.unit_cost} onChange={e => updateLine(idx, { unit_cost: Number(e.target.value) })} />
                    </td>
                    <td>
                      <div className="muted">{(Number(line.total_amount) || (Number(line.qty || 0) * Number(line.unit_cost || 0))).toFixed(2)}</div>
                    </td>
                    <td>
                      <button type="button" className="btn btn-danger small-btn" onClick={() => removeLine(idx)} disabled={form.lines.length === 1}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: 12 }}>
              <button type="button" className="add-line" onClick={() => addLine()}>Add line item</button>
            </div>
          </div>
        </Section>

        <div className="form-actions" style={{ marginTop: 16 }}>
          <div style={{ flex: 1 }}>
            <button type="button" className="btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
          </div>
          <div>
            <div style={{ marginBottom: 8, textAlign: "right" }}><strong>Total: {saleTotal.toFixed(2)}</strong></div>
            <button type="submit" className="btn btn-success" disabled={saving}>{saving ? "Saving..." : isEdit ? "Update Invoice" : "Create Invoice"}</button>
          </div>
        </div>
      </form>

      {/* Receipt modal kept unchanged */}
      {showReceipt && (
        <div className="receipt-overlay" role="dialog" aria-modal="true">
          <div className="receipt-modal">
            <div className="receipt-header">
              <h3>Invoice Receipt</h3>
              <button className="btn-ghost small-btn" onClick={() => setShowReceipt(false)}>×</button>
            </div>
            <div className="receipt-body">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <div><strong>Invoice:</strong> {form.invoice_number || "-"}</div>
                <div><strong>Date:</strong> {form.invoice_datetime ? new Date(form.invoice_datetime).toLocaleString() : "-"}</div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <div><strong>Customer:</strong> {customers.find(c => c.id === form.customer_id)?.name || form.buyer_name || "-"}</div>
                <div><strong>Status:</strong> <span className={`payment-status ${form.payment_status}`}>{form.payment_status || "pending"}</span></div>
              </div>
              <table className="receipt-table">
                <thead>
                  <tr><th>Product</th><th>Qty</th><th>Unit Price</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  {form.lines.map((l,i) => (
                    <tr key={i}><td>{l.product_name || "Unnamed Product"}</td><td>{l.qty}</td><td>{Number(l.unit_cost||0).toFixed(2)}</td><td>{Number(l.total_amount|| (l.qty*l.unit_cost)).toFixed(2)}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="receipt-total">Total: {saleTotal.toFixed(2)}</div>
            </div>
            <div className="receipt-footer">
              <button className="btn-ghost small-btn" onClick={() => setShowReceipt(false)}>Close</button>
              <button className="btn btn-success small-btn" onClick={printReceipt}>Print</button>
              <button className="btn small-btn" onClick={() => { setShowReceipt(false); navigate("/sales"); }}>Go to Sales</button>
            </div>
          </div>
        </div>
      )}

      {/* Invoices list below form (unchanged) */}
      {/* <div style={{ marginTop: 36 }}>
        <h3>Invoices</h3>
        <div style={{ marginBottom: 12 }}>
          <button className="btn small-btn" onClick={() => loadInvoices()} disabled={loadingInvoices}>{loadingInvoices ? "Refreshing..." : "Refresh list"}</button>
        </div>

        <table className="compact-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Total</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 12 }}>No invoices found.</td></tr>
            )}
            {invoices.map(inv => (
              <tr key={inv.id}>
                <td>{inv.invoice_number || "-"}</td>
                <td>{inv.customer?.name || inv.buyer_name || "-"}</td>
                <td>{Number(inv.total_amount || 0).toFixed(2)}</td>
                <td>{inv.payment_status || "-"}</td>
                <td>{inv.date ? new Date(inv.date).toLocaleString() : "-"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="btn-ghost small-btn" onClick={() => viewInvoice(inv.id)}>View</button>
                  <button className="btn small-btn" onClick={() => editInvoice(inv.id)}>Edit</button>
                  <button className="btn btn-danger small-btn" onClick={() => handleDeleteInvoice(inv.id)} disabled={deletingId === inv.id}>
                    {deletingId === inv.id ? "Deleting..." : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div> */}
    </div>
  );
}


export default function InvoiceForm(props) {
  const { noLayout } = props || {};
  const content = <InvoiceFormContent {...props} />;

  if (noLayout) return content;

  return <DashboardLayout>{content}</DashboardLayout>;
}


